import { useApp } from '@libs/hooks';
import { TCommonDataObj, THandleClick } from '@libs/types';
import { ConditionalRender } from '@libs/utils';
import React, { ReactNode, useState } from 'react';
import { TConsumerItem, TListDataMessages } from '../../types/data';
import { TransactionStatusEnum } from '../../types/enums';
import { TListOption } from '../../types/options';
import { RowTransaction } from '../UI/Row/RowTransaction';
import { SeparatorView } from '../Separator/SeparatorView';
import styles from './styles/styles.module.css';
import { resolveArguments } from './utils/transactionUtils';

type TListItemsProps = {
    items: Array<TConsumerItem>;
    wid: string;
    messages: TListDataMessages;
    handleClick: THandleClick;
} & Pick<TListOption, 'state'>;
type SortedItem = {
    separator?: ReactNode;
} & TConsumerItem;

function TransactionsListItems({ wid, items, messages, handleClick, state = 'FULL' }: TListItemsProps) {
    const { language } = useApp();
    const [currentDate] = useState(new Date());
    const [yesterdayDate] = useState(new Date(new Date().setDate(currentDate.getDate() - 1)));

    const sortTransactions = (transactions: TConsumerItem[]): SortedItem[] => {
        const sortedArr = Array.isArray(transactions)
            ? [...transactions].sort((a, b) => {
                  if (a?.status === TransactionStatusEnum.AWAITING && b?.status !== TransactionStatusEnum.AWAITING) {
                      return -1;
                  } else if (a?.status !== TransactionStatusEnum.AWAITING && b?.status === TransactionStatusEnum.AWAITING) {
                      return 1;
                  } else {
                      const dateA = a?.date instanceof Date ? a?.date : new Date(a?.date as string);
                      const dateB = b?.date instanceof Date ? b?.date : new Date(b?.date as string);
                      return dateB.getTime() - dateA.getTime();
                  }
              })
            : [];
        const result = sortedArr.reduce(
            (acc, item) => {
                let separator = null;
                if (item?.status === TransactionStatusEnum.AWAITING) {
                    if (!acc.awaitingAdded) {
                        acc.awaitingAdded = true;
                        separator = <SeparatorView text={messages?.awaiting} variant="subheader" />;
                    }
                } else {
                    const date = item?.date as Date;
                    if (date?.getFullYear?.()) {
                        const month = date.getMonth().toString();
                        const monthAndYear = `${month}_${date.getFullYear()}`;
                        const monthAndDay = `${month}_${date.getDate()}`;
                        if (date.toDateString() === currentDate.toDateString()) {
                            if (!acc.todayAdded) {
                                acc.todayAdded = true;
                                separator = <SeparatorView text={messages?.today} variant="subheader" />;
                            }
                        } else if (date.toDateString() === yesterdayDate.toDateString()) {
                            if (!acc.yesterdayAdded) {
                                acc.yesterdayAdded = true;
                                separator = <SeparatorView text={messages?.yesterday} variant="subheader" />;
                            }
                        } else if (currentDate.getFullYear() === date.getFullYear() && currentDate.getMonth() === date.getMonth()) {
                            if (!acc.currentMonthAdded[monthAndDay]) {
                                acc.currentMonthAdded[monthAndDay] = true;
                                separator = (
                                    <SeparatorView text={resolveArguments(messages?.monthList?.[date.getMonth()], date.getDate())} variant="subheader" />
                                );
                            }
                        } else if (currentDate.getFullYear() === date.getFullYear()) {
                            if (!acc.currentYearAdded[monthAndYear]) {
                                acc.currentYearAdded[monthAndYear] = true;
                                separator = (
                                    <SeparatorView text={date.toLocaleString(language ?? 'default', { month: 'long' })} variant="subheader" />
                                );
                            }
                        } else if (!acc.moreThanYearAdded[monthAndYear]) {
                            acc.moreThanYearAdded[monthAndYear] = true;
                            separator = (
                                <SeparatorView text={`${date.toLocaleString(language ?? 'default', { month: 'long' })} ${date.getFullYear()}`} variant="subheader" />
                            );
                        }
                    }
                }
                acc.newArr.push({ ...item, separator });
                return acc;
            },
            {
                newArr: [] as SortedItem[],
                awaitingAdded: false,
                todayAdded: false,
                yesterdayAdded: false,
                currentMonthAdded: {} as TCommonDataObj,
                currentYearAdded: {} as TCommonDataObj,
                moreThanYearAdded: {} as TCommonDataObj,
            },
        );
        return result.newArr;
    };

    if (items?.length > 0) {
        return sortTransactions(items).map((item: SortedItem, index) => {
            const isClickable = item.clickable !== false;
            return (
                <div className={styles.transactionListItemWrapper} key={`${index}_${item?.id}`}>
                    <ConditionalRender condition={state === 'FULL'}>
                        <div className={styles.transactionListItemSeparatorWrapper}>{item.separator ?? ''}</div>
                    </ConditionalRender>
                    <RowTransaction as={isClickable ? 'button' : 'div'} item={item} handleClick={isClickable ? () => handleClick(wid, item) : undefined} />
                </div>
            );
        });
    }
    return null;
}

export default TransactionsListItems;
